// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, xClassNames } from "@steiner/XDL/Utilities";
import React, { FC, useState } from "react";

export interface HeaderClickableItemProps {
    isOpen?: boolean;
    isCompactHeader?: boolean;
    additionalElement?: React.ReactNode;
    extendedStyles?: HeaderClickableStyles;
}

export type HeaderClickableProps = HeaderClickableItemProps & ClickableProps;

type Props = HeaderClickableProps;

export interface HeaderClickableStyles {
    clickable?: ClickablePresentationProps;
    iconWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    title?: TypographyPresentationProps;
    openColor?: TypographyProps["color"];
}

export const headerClickableStyles: HeaderClickableStyles = {};

const HeaderClickable: FC<Props> = ({
    isOpen,
    isCompactHeader,
    additionalElement,
    children,
    extendedStyles,
    ...otherProps
}) => {
    const { isInShell } = useShellContext();
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));
    const className = xClassNames.append([isOpen && "opened"], otherProps.className);

    return (
        <Clickable {...styles.clickable} {...otherProps} className={className} {...{ isCompactHeader, isInShell }}>
            {styles.icon?.src && (
                <StyledWrapper {...styles.iconWrapper}>
                    <Icon
                        {...styles.icon}
                        color={isOpen ? styles.openColor : styles.icon.color}
                        {...{ isCompactHeader, isInShell }}
                    />
                    {additionalElement}
                </StyledWrapper>
            )}
            <Typography
                {...styles.title}
                color={isOpen ? styles.openColor : styles.title?.color}
                {...{ isCompactHeader, isInShell }}
            >
                {children}
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
